import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box, Card, CardActionArea, CardContent, Stack, Typography, Chip,
  CircularProgress, Alert, Grid, Tooltip, Grow, IconButton,
  Divider, TextField, InputAdornment, LinearProgress, Button,
} from '@mui/material';
import { useTheme, alpha } from '@mui/material/styles';
import {
  MenuBook as SubjectIcon, ClassOutlined as ClassIcon,
  EditNote as EnterIcon, Lock as LockIcon,
  OpenInFull as DialogIcon, KeyboardArrowDown as PulldownIcon,
  Search as SearchIcon, Assessment as ExamIcon,
  EmojiObjects as MidIcon, BoltOutlined as MockIcon,
  WorkspacePremium as ReportCardIcon,
  AccountBalanceWalletOutlined as SubjectsStatIcon,
  Class as ClassesStatIcon, EventNote as PeriodStatIcon,
  ChevronRight as ChevronIcon,
  FileDownload as ExportIcon, CloudUpload as ImportIcon,
} from '@mui/icons-material';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowBack as ArrowBackIcon, Visibility as ViewAsIcon } from '@mui/icons-material';
import API from '../BaseURL';
import swal from '../utils/swal';
import RawScoreSheetDialog from '../pages/examination/RawScoreSheetDialog';
import RemarksSheetDialog  from '../pages/examination/RemarksSheetDialog';
import TeacherImportDialog from './TeacherImportDialog';
import ExamReportDialog   from './ExamReportDialog';

// TeacherPortalHome — every (Class × Subject × Exam-Type) the teacher
// owns gets its OWN card, grouped under one of three bold exam-type
// sections (Term Exam, Mid-Term, Mock). No state switcher at the top
// — the card itself declares its exam type, so a teacher always knows
// at a glance exactly which sheet they're about to open.
//
// Per card, two ways to open the same score sheet:
//   • OpenInFull icon       → centered modal dialog
//   • KeyboardArrowDown icon → full-screen pull-down sheet
//     (best on phones — slides in from the top)

const STATES = [
  { key: 'Term Exam', label: 'TERM EXAM',
    icon: <ExamIcon sx={{ fontSize: 20 }} />,
    from: '#5e7cff', to: '#1a237e' },                // indigo
  { key: 'Mid-Term',  label: 'MID-TERM',
    icon: <MidIcon  sx={{ fontSize: 20 }} />,
    from: '#ffb74d', to: '#ef6c00' },                // amber
  { key: 'Mock',      label: 'MOCK',
    icon: <MockIcon sx={{ fontSize: 20 }} />,
    from: '#66bb6a', to: '#2e7d32' },                // green
];

// Which section an exam belongs under.
//
// The section keys above are display names; `ex_re.state` holds what
// was actually registered, and the two are not the same string —
// registration stores "Mid-Term Test" while the section is keyed
// "Mid-Term", so an exact match dropped every mid-term card on the
// floor without a word. Matched on the stem instead, which also keeps
// older rows saying plain "Mid-Term" working.
const sectionOf = (s) => {
  const v = String(s || '').trim().toLowerCase();
  if (v.startsWith('mock')) return 'Mock';
  if (v.startsWith('mid'))  return 'Mid-Term';
  return 'Term Exam';
};

// Deterministic pastel tint per class so each class keeps its visual
// identity even when it appears under multiple exam-type sections.
function classTint(s) {
  const TINTS = [
    { from: '#5e7cff', to: '#7c4dff' },
    { from: '#ff6f91', to: '#e91e63' },
    { from: '#26c6da', to: '#0288d1' },
    { from: '#66bb6a', to: '#2e7d32' },
    { from: '#ffb74d', to: '#ef6c00' },
    { from: '#ba68c8', to: '#7b1fa2' },
  ];
  let h = 0;
  for (let i = 0; i < (s || '').length; i++) h = (h * 31 + s.charCodeAt(i)) | 0;
  return TINTS[Math.abs(h) % TINTS.length];
}

export default function TeacherPortalHome() {
  const theme = useTheme();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // View-as mode — an admin previewing another teacher's portal. The
  // ?view_as=<user_id> param is forwarded to /me so the backend
  // returns THAT teacher's assignments instead of the caller's own.
  // TeacherGuard is responsible for letting admins through when this
  // param is set.
  const viewAsId = searchParams.get('view_as');

  const [period, setPeriod]       = useState({ acc_yer: '', term: '' });
  const [assignments, setAssignments] = useState([]);
  const [viewedTeacher, setViewedTeacher] = useState(null);   // {id, name, email} when viewing as
  const [classTeacherClasses, setClassTeacherClasses] = useState([]);   // ['JHS 1', 'JHS 2']
  const [remarksBucket, setRemarksBucket] = useState(null);   // { cla, term, acc_yer }
  const [reportBucket,  setReportBucket]  = useState(null);   // { cla, term, acc_yer }
  const [loading, setLoading]     = useState(true);
  const [q, setQ]                 = useState('');
  const [openBucket, setOpenBucket] = useState(null);
  const [dialogMode, setDialogMode] = useState('modal');
  // Separate bucket for the silent "Export Excel" path — opens the
  // same RawScoreSheetDialog with autoExport=true so it downloads and
  // closes without the teacher seeing the editor.
  const [exportBucket, setExportBucket] = useState(null);
  // Bulk-import dialog state.
  const [importOpen, setImportOpen] = useState(false);

  const loadMe = useCallback(async () => {
    setLoading(true);
    try {
      const me = await API.get('/teacher-assignments/me', {
        params: viewAsId ? { view_as: viewAsId } : undefined,
      });
      setPeriod({ acc_yer: me.data?.acc_yer || '', term: me.data?.term || '' });
      setAssignments(me.data?.assignments || []);
      setViewedTeacher(me.data?.view_as || null);
      setClassTeacherClasses(Array.isArray(me.data?.class_teacher_classes) ? me.data.class_teacher_classes : []);

      // Hub-parity guard: hide this route from anyone who has no work
      // to do here, or whose editing window has already closed. Admins
      // (admin:true) and view-as previews are allowed through so the
      // preview workflow still functions. Same rule the hub cards use.
      const isAdmin       = !!me.data?.admin;
      const isViewingAs   = !!me.data?.view_as;
      const hasWork       = (me.data?.assignments?.length > 0)
                          || (me.data?.class_teacher_classes?.length > 0);
      const periodOpen    = me.data?.period_open !== false;
      if (!isAdmin && !isViewingAs && (!hasWork || !periodOpen)) {
        navigate('/teacher-portal', { replace: true });
      }
    } catch (e) {
      swal.error(swal.pickError(e, 'Could not load your assignments'));
    } finally {
      setLoading(false);
    }
  }, [viewAsId, navigate]);
  useEffect(() => { loadMe(); }, [loadMe]);

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return assignments;
    return assignments.filter(a =>
      a.sub.toLowerCase().includes(needle)
      || a.cla.toLowerCase().includes(needle)
    );
  }, [assignments, q]);

  // Build per-state groupings — but ONLY include an assignment under
  // a state if the backend told us that state actually has a row in
  // ex_re for this (cla, sub, term, acc_yer). The backend now ships
  // `a.states` as an array of { state, entered, total, pct } so each
  // card can also render its progress bar.
  const groupsByState = useMemo(() => {
    const out = new Map();
    for (const a of filtered) {
      const states = Array.isArray(a.states) ? a.states : [];
      for (const stEntry of states) {
        // Grouped by the EXAM TYPE, which is what the section headers
        // are keyed on. The SITTING lives on the card instead — a mock
        // that was sat twice is two cards under one MOCK heading, each
        // labelled with its number, rather than two headings.
        const raw = typeof stEntry === 'string' ? stEntry : stEntry?.state;
        if (!raw) continue;
        const stateKey = sectionOf(raw);
        if (!out.has(stateKey)) out.set(stateKey, new Map());
        const byClass = out.get(stateKey);
        if (!byClass.has(a.cla)) byClass.set(a.cla, []);
        // Attach the per-state progress to the card payload so the
        // card component can render its progress bar without another
        // lookup.
        byClass.get(a.cla).push({
          ...a,
          progress: typeof stEntry === 'object' ? stEntry : null,
        });
      }
    }
    return out;
  }, [filtered]);

  // Assignments the teacher owns but that have NO registered subject
  // for the active term — surfaced so they aren't silently dropped.
  const unregistered = useMemo(
    () => filtered.filter(a => !Array.isArray(a.states) || a.states.length === 0),
    [filtered]
  );

  const totals = useMemo(() => ({
    subjects: assignments.length,
    classes:  new Set(assignments.map(a => a.cla)).size,
  }), [assignments]);

  // `examState` may arrive as the plain exam name or as the progress
  // entry carrying its sitting. The sheet needs BOTH: the exam type to
  // build its columns, and the sitting so it reads and writes the right
  // paper's marks rather than the previous mock's.
  const openSheet = useCallback((cla, examState, mode) => {
    if (!period.acc_yer || !period.term) {
      swal.warning('No active academic period — ask your administrator to set one.');
      return;
    }
    const state    = typeof examState === 'object' ? examState.state : examState;
    const exam_num = typeof examState === 'object' ? examState.exam_num : undefined;
    setDialogMode(mode);
    setOpenBucket({
      cla, term: period.term, state, acc_yer: period.acc_yer,
      ...(exam_num ? { exam_num } : {}),
    });
  }, [period]);

  // Silent per-card export. Mounts a hidden RawScoreSheetDialog with
  // autoExport=true; once the data loads it triggers the .xlsx
  // download and closes, so the teacher just sees the file land.
  const triggerExport = useCallback((cla, examState) => {
    if (!period.acc_yer || !period.term) {
      swal.warning('No active academic period — ask your administrator to set one.');
      return;
    }
    const state    = typeof examState === 'object' ? examState.state : examState;
    const exam_num = typeof examState === 'object' ? examState.exam_num : undefined;
    setExportBucket({
      cla, term: period.term, state, acc_yer: period.acc_yer,
      ...(exam_num ? { exam_num } : {}),
    });
  }, [period]);

  if (loading) {
    return (
      <Box sx={{ textAlign: 'center', py: 10 }}>
        <CircularProgress />
        <Typography sx={{ mt: 2 }} color="text.secondary">Loading your subjects…</Typography>
      </Box>
    );
  }

  return (
    <Box>
      {/* View-as banner — admin previewing another teacher's portal */}
      {viewAsId && (
        <Card sx={{
          mb: 2, p: 1.5, borderRadius: 2,
          bgcolor: 'warning.50', border: '2px solid', borderColor: 'warning.main',
        }}>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.2}
            alignItems={{ sm: 'center' }} justifyContent="space-between">
            <Stack direction="row" spacing={1.2} alignItems="center" sx={{ flex: 1, minWidth: 0 }}>
              <ViewAsIcon sx={{ color: 'warning.dark' }} />
              <Box sx={{ minWidth: 0 }}>
                <Typography sx={{ fontWeight: 800, fontSize: 14, lineHeight: 1.2, color: 'warning.dark' }}>
                  Viewing as {viewedTeacher?.name || 'teacher'}
                </Typography>
                <Typography variant="caption" sx={{ color: 'warning.dark', opacity: 0.8, display: 'block', lineHeight: 1.2 }}>
                  {viewedTeacher?.email || 'Read-only preview from Examination Settings'} — you're seeing the portal exactly as they see it.
                </Typography>
              </Box>
            </Stack>
            <Button size="small" variant="contained" color="warning"
              startIcon={<ArrowBackIcon />}
              onClick={() => navigate('/examination/settings')}
              sx={{ textTransform: 'none', fontWeight: 800, flexShrink: 0 }}>
              Back to settings
            </Button>
          </Stack>
        </Card>
      )}

      {/* Summary strip */}
      <Card sx={{
        mb: 2.4, p: { xs: 1.6, sm: 2 }, borderRadius: 3,
        bgcolor: 'rgba(255,255,255,0.85)',
        backdropFilter: 'blur(8px)',
        border: '1px solid', borderColor: 'divider',
        boxShadow: '0 1px 4px rgba(0,0,0,0.04)',
      }}>
        <Grid container spacing={{ xs: 1.5, sm: 2 }} alignItems="center">
          <Grid item xs={12} sm={6} md={4}>
            <SummaryStat
              icon={<PeriodStatIcon />}
              label="Active period"
              value={`${period.acc_yer || '—'} · ${(period.term || '').replace(/_/g, ' ') || '—'}`}
              tint={theme.palette.primary.main}
              chip={<Chip size="small" icon={<LockIcon sx={{ fontSize: 12 }} />}
                label="Year-locked"
                sx={{ height: 20, fontWeight: 700, fontSize: 10 }} />} />
          </Grid>
          <Grid item xs={6} sm={3} md={4}>
            <SummaryStat
              icon={<SubjectsStatIcon />}
              label="Your subjects"
              value={String(totals.subjects)}
              tint={theme.palette.secondary.main} />
          </Grid>
          <Grid item xs={6} sm={3} md={4}>
            <SummaryStat
              icon={<ClassesStatIcon />}
              label="Classes"
              value={String(totals.classes)}
              tint="#1b5e20" />
          </Grid>
        </Grid>
      </Card>

      {/* Toolbar: bulk-import button + subject search */}
      {assignments.length > 0 && (
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.2}
          sx={{ mb: 2.4 }} alignItems={{ xs: 'stretch', sm: 'center' }}>
          <Button
            variant="contained" size="medium"
            startIcon={<ImportIcon />}
            onClick={() => setImportOpen(true)}
            sx={{
              textTransform: 'none', fontWeight: 800, borderRadius: 99, px: 2.4,
              background: (t) => `linear-gradient(135deg, ${t.palette.primary.main}, ${t.palette.secondary.main})`,
              boxShadow: (t) => `0 8px 20px -10px ${alpha(t.palette.primary.main, 0.6)}`,
            }}>
            Import filled Excel
          </Button>
          <Box sx={{ flex: 1 }} />
          <TextField
            size="small" placeholder="Search subject or class…"
            value={q} onChange={e => setQ(e.target.value)}
            sx={{
              minWidth: { xs: '100%', md: 320 },
              bgcolor: 'rgba(255,255,255,0.85)',
              backdropFilter: 'blur(8px)',
              '& .MuiOutlinedInput-root': { borderRadius: 99 },
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment>
              ),
            }} />
        </Stack>
      )}

      {assignments.length === 0 && (
        <Card sx={{ p: 4, borderRadius: 3, textAlign: 'center',
          border: '1px dashed', borderColor: 'primary.light',
          bgcolor: alpha(theme.palette.primary.main, 0.04) }}>
          <SubjectIcon sx={{ fontSize: 48, color: 'primary.light', mb: 1 }} />
          <Typography fontWeight={800} sx={{ fontSize: 18, mb: 0.6 }}>
            No subjects assigned yet
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 480, mx: 'auto' }}>
            Ask your administrator to open <b>Examination Settings → Teacher Assignments</b>
            and add the classes / subjects you teach for {period.acc_yer || 'this academic year'}.
          </Typography>
        </Card>
      )}

      {filtered.length === 0 && assignments.length > 0 && (
        <Alert severity="info" variant="outlined" sx={{ borderRadius: 2 }}>
          No subjects match "{q}". Clear the search to see everything.
        </Alert>
      )}

      {/* Class-teacher only — a Remarks card per class this teacher owns
          as homeroom/form-master. Sits above the subject cards because
          the class-teacher role is a higher-level responsibility. */}
      {classTeacherClasses.length > 0 && (
        <Box sx={{ mb: { xs: 3.6, sm: 4.4 } }}>
          <Stack direction="row" alignItems="center" spacing={1.2} sx={{ mb: 2 }}>
            <Box sx={{
              display: 'inline-flex', alignItems: 'center', gap: 1,
              px: 1.8, py: 0.9, borderRadius: 2,
              background: 'linear-gradient(135deg, #7c4dff 0%, #4527a0 100%)',
              color: '#fff', boxShadow: '0 2px 8px rgba(124,77,255,0.35)',
            }}>
              <EnterIcon sx={{ fontSize: 20 }} />
              <Typography sx={{ fontWeight: 800, fontSize: 13, letterSpacing: 1, textTransform: 'uppercase' }}>
                Class Remarks
              </Typography>
            </Box>
            <Chip size="small" variant="outlined"
              label={`Class teacher of ${classTeacherClasses.length}`}
              sx={{ fontWeight: 700 }} />
          </Stack>
          <Grid container spacing={{ xs: 1.5, sm: 2 }}>
            {classTeacherClasses.map((cla) => {
              const tint = classTint(cla);
              return (
                <Grid item xs={12} sm={6} md={4} lg={3} key={cla}>
                  <Grow in timeout={300}>
                    <Card sx={{
                      borderRadius: 3, overflow: 'hidden',
                      border: '1px solid', borderColor: 'divider',
                      boxShadow: '0 1px 4px rgba(0,0,0,0.05)',
                      '&:hover': { boxShadow: '0 6px 22px rgba(124,77,255,0.25)', transform: 'translateY(-2px)' },
                      transition: 'all 0.18s',
                    }}>
                      <CardActionArea
                        onClick={() => {
                          if (!period.acc_yer || !period.term) {
                            swal.warning('No active academic period — ask your administrator to set one.');
                            return;
                          }
                          setRemarksBucket({ cla, term: period.term, acc_yer: period.acc_yer });
                        }}>
                        <Box sx={{
                          px: 1.6, py: 1.2,
                          background: `linear-gradient(135deg, ${tint.from} 0%, ${tint.to} 100%)`,
                          color: '#fff',
                        }}>
                          <Stack direction="row" alignItems="center" spacing={1}>
                            <ClassIcon sx={{ fontSize: 18 }} />
                            <Typography sx={{ fontWeight: 800, fontSize: 15, flex: 1 }}>{cla}</Typography>
                          </Stack>
                          <Typography variant="caption" sx={{ opacity: 0.9, display: 'block', mt: 0.3 }}>
                            {(period.term || '').replace('_', ' ')} · {period.acc_yer}
                          </Typography>
                        </Box>
                        <CardContent sx={{ p: 1.4, '&:last-child': { pb: 1.4 } }}>
                          <Stack direction="row" alignItems="center" spacing={0.8}>
                            <EnterIcon sx={{ fontSize: 18, color: 'primary.main' }} />
                            <Box sx={{ flex: 1 }}>
                              <Typography sx={{ fontWeight: 700, fontSize: 13, lineHeight: 1.2 }}>
                                Open remarks sheet
                              </Typography>
                              <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.2 }}>
                                Conduct · Attitude · Class Teacher · Headmaster · Attendance
                              </Typography>
                            </Box>
                            <ChevronIcon sx={{ color: 'action.active' }} />
                          </Stack>
                        </CardContent>
                      </CardActionArea>
                    </Card>
                  </Grow>
                </Grid>
              );
            })}
          </Grid>
        </Box>
      )}

      {/* Examination Report Cards — one card per class this teacher owns
          as homeroom/form-master. Opens a dialog where the class teacher
          picks a student and previews their full report card with print
          and Word download. Reuses the same reportCardHTML the admin
          Student Report page uses, so the printed sheet is identical. */}
      {classTeacherClasses.length > 0 && (
        <Box sx={{ mb: { xs: 3.6, sm: 4.4 } }}>
          <Stack direction="row" alignItems="center" spacing={1.2} sx={{ mb: 2 }}>
            <Box sx={{
              display: 'inline-flex', alignItems: 'center', gap: 1,
              px: 1.8, py: 0.9, borderRadius: 2,
              background: 'linear-gradient(135deg, #ec4899 0%, #9d174d 100%)',
              color: '#fff', boxShadow: '0 2px 8px rgba(236,72,153,0.35)',
            }}>
              <ReportCardIcon sx={{ fontSize: 20 }} />
              <Typography sx={{ fontWeight: 800, fontSize: 13, letterSpacing: 1, textTransform: 'uppercase' }}>
                Exam Report Cards
              </Typography>
            </Box>
            <Chip size="small" variant="outlined"
              label={`${classTeacherClasses.length} class${classTeacherClasses.length === 1 ? '' : 'es'}`}
              sx={{ fontWeight: 700 }} />
          </Stack>
          <Grid container spacing={{ xs: 1.5, sm: 2 }}>
            {classTeacherClasses.map((cla) => {
              const tint = classTint(cla);
              return (
                <Grid item xs={12} sm={6} md={4} lg={3} key={cla}>
                  <Grow in timeout={300}>
                    <Card sx={{
                      borderRadius: 3, overflow: 'hidden',
                      border: '1px solid', borderColor: 'divider',
                      boxShadow: '0 1px 4px rgba(0,0,0,0.05)',
                      '&:hover': { boxShadow: '0 6px 22px rgba(236,72,153,0.25)', transform: 'translateY(-2px)' },
                      transition: 'all 0.18s',
                    }}>
                      <CardActionArea
                        onClick={() => {
                          if (!period.acc_yer || !period.term) {
                            swal.warning('No active academic period — ask your administrator to set one.');
                            return;
                          }
                          setReportBucket({ cla, term: period.term, acc_yer: period.acc_yer });
                        }}>
                        <Box sx={{
                          px: 1.6, py: 1.2,
                          background: `linear-gradient(135deg, ${tint.from} 0%, ${tint.to} 100%)`,
                          color: '#fff',
                        }}>
                          <Stack direction="row" alignItems="center" spacing={1}>
                            <ClassIcon sx={{ fontSize: 18 }} />
                            <Typography sx={{ fontWeight: 800, fontSize: 15, flex: 1 }}>{cla}</Typography>
                          </Stack>
                          <Typography variant="caption" sx={{ opacity: 0.9, display: 'block', mt: 0.3 }}>
                            {(period.term || '').replace('_', ' ')} · {period.acc_yer}
                          </Typography>
                        </Box>
                        <CardContent sx={{ p: 1.4, '&:last-child': { pb: 1.4 } }}>
                          <Stack direction="row" alignItems="center" spacing={0.8}>
                            <ReportCardIcon sx={{ fontSize: 18, color: '#ec4899' }} />
                            <Box sx={{ flex: 1 }}>
                              <Typography sx={{ fontWeight: 700, fontSize: 13, lineHeight: 1.2 }}>
                                Open report cards
                              </Typography>
                              <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.2 }}>
                                Subjects · Grades · Position · Conduct · Remarks · Print
                              </Typography>
                            </Box>
                            <ChevronIcon sx={{ color: 'action.active' }} />
                          </Stack>
                        </CardContent>
                      </CardActionArea>
                    </Card>
                  </Grow>
                </Grid>
              );
            })}
          </Grid>
        </Box>
      )}

      {/* One exam-type section per state that actually has subjects
          registered for this teacher. States with nothing registered
          are not rendered at all — no replication. */}
      {filtered.length > 0 && STATES.map((s, si) => {
        const byClass = groupsByState.get(s.key);
        if (!byClass || byClass.size === 0) return null;
        const groups = [...byClass.entries()]
          .sort((a, b) => a[0].localeCompare(b[0]))
          .map(([cla, subs]) => ({
            cla,
            subs: subs.sort((x, y) => x.sub.localeCompare(y.sub)),
            tint: classTint(cla),
          }));
        return (
          <ExamTypeSection key={s.key}
            state={s}
            groups={groups}
            onOpen={openSheet}
            onExport={triggerExport}
            delay={si * 60} />
        );
      })}

      {/* Surface assignments with no registration so the teacher
          knows to ask the admin to register the subject for the
          current term — beats silently hiding the card. */}
      {unregistered.length > 0 && (
        <Alert severity="info" variant="outlined" sx={{ borderRadius: 2, mt: 2 }}>
          <Typography fontWeight={800} sx={{ mb: 0.5 }}>
            Waiting on subject registration ({unregistered.length})
          </Typography>
          <Typography variant="body2" sx={{ mb: 1 }}>
            These subjects are assigned to you but haven't been registered
            for any exam type in <b>{period.term?.replace(/_/g, ' ')}</b>.
            Ask the administrator to register them in
            <b> Examination → Register Subject</b> first.
          </Typography>
          <Stack direction="row" spacing={0.6} sx={{ flexWrap: 'wrap', rowGap: 0.6 }}>
            {unregistered.map(a => (
              <Chip key={a.id} size="small" variant="outlined"
                label={`${a.cla} · ${a.sub}`}
                sx={{ fontWeight: 700 }} />
            ))}
          </Stack>
        </Alert>
      )}

      <RawScoreSheetDialog
        open={!!openBucket}
        onClose={() => setOpenBucket(null)}
        bucket={openBucket}
        mode={dialogMode}
      />

      <RemarksSheetDialog
        open={!!remarksBucket}
        onClose={() => setRemarksBucket(null)}
        bucket={remarksBucket}
      />

      {/* Hidden dialog used only for the silent per-card Export. The
          dialog itself flashes very briefly while data loads, then
          auto-downloads the .xlsx and closes itself. */}
      <RawScoreSheetDialog
        open={!!exportBucket}
        onClose={() => setExportBucket(null)}
        bucket={exportBucket}
        autoExport={true}
      />

      {/* Bulk Excel import */}
      <TeacherImportDialog
        open={importOpen}
        onClose={() => setImportOpen(false)}
        onCommitted={() => { setImportOpen(false); loadMe(); }}
      />

      {/* Exam report cards — full-screen preview + print/word */}
      <ExamReportDialog
        open={!!reportBucket}
        onClose={() => setReportBucket(null)}
        bucket={reportBucket}
      />
    </Box>
  );
}

// One exam-type section: bold coloured header + a grid of cards
// per class. Same cards repeat under each section — the only thing
// that changes is the state passed to openSheet.
function ExamTypeSection({ state, groups, onOpen, onExport, delay = 0 }) {
  return (
    <Box sx={{ mb: { xs: 3.6, sm: 4.4 } }}>
      {/* Bold exam-type header — gradient pill so it reads at a
          glance: "this whole block is TERM EXAM". */}
      <Stack direction="row" alignItems="center" spacing={1.2}
        sx={{ mb: 2 }}>
        <Box sx={{
          display: 'inline-flex', alignItems: 'center', gap: 1,
          px: 2, py: 0.9, borderRadius: 99,
          background: `linear-gradient(135deg, ${state.from}, ${state.to})`,
          color: '#fff', fontWeight: 900,
          fontSize: { xs: 13, sm: 14.5 }, letterSpacing: 1.2,
          boxShadow: `0 8px 20px -10px ${alpha(state.to, 0.6)}`,
        }}>
          {state.icon}
          {state.label}
        </Box>
        <Box sx={{
          flex: 1, height: 1,
          background: `linear-gradient(90deg, ${alpha(state.to, 0.35)}, transparent)`,
        }} />
      </Stack>

      {/* Per-class groups */}
      {groups.map((g, gi) => (
        <Box key={`${state.key}-${g.cla}`} sx={{ mb: 2.4 }}>
          <Stack direction="row" alignItems="center" spacing={1}
            sx={{ mb: 1.2 }}>
            <Box sx={{
              px: 1.4, py: 0.55, borderRadius: 99,
              background: `linear-gradient(135deg, ${g.tint.from}, ${g.tint.to})`,
              color: '#fff', fontWeight: 800,
              fontSize: 13.5, letterSpacing: 0.3,
              boxShadow: `0 4px 12px -8px ${alpha(g.tint.to, 0.6)}`,
            }}>
              {g.cla}
            </Box>
            {/* Counts SUBJECTS, not cards. One subject sat twice is two
                cards, and calling that "2 subjects" was the thing that
                made a repeated mock look like duplicated data. */}
            {(() => {
              const nSubs  = new Set(g.subs.map(x => x.sub)).size;
              const extra  = g.subs.length - nSubs;
              return (
                <Chip size="small" variant="outlined"
                  label={`${nSubs} subject${nSubs === 1 ? '' : 's'}`
                    + (extra > 0 ? ` · ${g.subs.length} sittings` : '')}
                  sx={{ fontWeight: 700, bgcolor: 'rgba(255,255,255,0.7)' }} />
              );
            })()}
          </Stack>

          <Grid container spacing={{ xs: 1.4, sm: 1.8 }}>
            {g.subs.map((a, i) => (
              // The sitting is part of the key: one subject sat twice is
              // two cards, and `a.id` alone is the same string for both.
              <Grid item xs={12} sm={6} md={4} lg={3}
                key={`${a.id}|${a.progress?.state || ''}|${a.progress?.exam_num || 1}`}>
                <Grow in timeout={250 + delay + (gi * 70) + (i * 50)}>
                  <Card sx={{
                    position: 'relative',
                    borderRadius: 3,
                    bgcolor: 'rgba(255,255,255,0.92)',
                    backdropFilter: 'blur(10px)',
                    border: '1px solid', borderColor: 'divider',
                    boxShadow: '0 1px 4px rgba(0,0,0,0.05)',
                    overflow: 'hidden',
                    transition: 'all 180ms ease',
                    '&:hover': {
                      transform: 'translateY(-3px)',
                      boxShadow: `0 14px 30px -12px ${alpha(state.to, 0.45)}`,
                      borderColor: state.to,
                    },
                    '&:hover .open-arrow': { transform: 'translateX(4px)' },
                  }}>
                    {/* Exam-type tint stripe (uses the SECTION's colour,
                        so each card visibly carries its exam type even
                        if the page is scrolled past the heading). */}
                    <Box sx={{
                      height: 4,
                      background: `linear-gradient(90deg, ${state.from}, ${state.to})`,
                    }} />
                    <CardActionArea onClick={() => onOpen(a.cla, a.progress || state.key, 'modal')}
                      sx={{ p: 0 }}>
                      <CardContent sx={{ p: { xs: 1.8, sm: 2 }, pb: '0 !important' }}>
                        <Stack direction="row" alignItems="center" spacing={1.4} sx={{ mb: 1.2 }}>
                          <Box sx={{
                            width: 38, height: 38, borderRadius: 1.5,
                            background: `linear-gradient(135deg, ${alpha(state.from, 0.18)}, ${alpha(state.to, 0.22)})`,
                            color: state.to,
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                          }}>
                            <SubjectIcon />
                          </Box>
                          <Typography sx={{
                            flex: 1, fontWeight: 800, fontSize: 16, lineHeight: 1.2,
                            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                          }}>
                            {a.sub}
                          </Typography>
                        </Stack>
                        <Stack direction="row" spacing={0.8}
                          sx={{ flexWrap: 'wrap', rowGap: 0.6 }}>
                          <Chip size="small" icon={<ClassIcon sx={{ fontSize: 14 }} />}
                            label={a.cla} sx={{ height: 22, fontWeight: 700 }} />
                          {/* The exam-type chip — bold, coloured, on
                              every card so the teacher can never mix
                              up which sheet they're opening. */}
                          {/* Carries the SITTING when there is one, so a
                              class that sat two mocks shows MOCK 1 and
                              MOCK 2 rather than the same word twice with
                              no way to tell which paper is which. */}
                          <Chip size="small" icon={state.icon}
                            label={(a.progress?.label || state.label).toUpperCase()}
                            sx={{
                              height: 22, fontWeight: 800, color: '#fff',
                              background: `linear-gradient(135deg, ${state.from}, ${state.to})`,
                              '& .MuiChip-icon': { color: '#fff' },
                            }} />
                        </Stack>

                        {/* Progress bar — shows how many of the class's
                            students already have at least one score
                            saved for this (subject, exam type). Backend
                            ships entered/total/pct on a.progress. */}
                        <ProgressMeter
                          progress={a.progress}
                          from={state.from}
                          to={state.to} />
                      </CardContent>
                    </CardActionArea>

                    <Divider sx={{ mt: 1.6 }} />
                    <Stack direction="row" alignItems="center"
                      sx={{ px: 1, py: 0.6, bgcolor: alpha(state.from, 0.06) }}>
                      <Stack direction="row" alignItems="center" spacing={0.5}
                        sx={{ flex: 1, pl: 1, color: state.to }}>
                        <EnterIcon sx={{ fontSize: 16 }} />
                        <Typography variant="caption" sx={{ fontWeight: 800, letterSpacing: 0.3 }}>
                          Open score sheet
                        </Typography>
                        <ChevronIcon className="open-arrow"
                          sx={{ fontSize: 16, transition: 'transform 180ms ease' }} />
                      </Stack>
                      <Tooltip title="Open as dialog (centered window)">
                        <IconButton size="small" sx={{ color: state.to }}
                          onClick={(e) => { e.stopPropagation(); onOpen(a.cla, a.progress || state.key, 'modal'); }}>
                          <DialogIcon sx={{ fontSize: 18 }} />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Pull down (full screen — best on phones)">
                        <IconButton size="small" sx={{ color: state.to }}
                          onClick={(e) => { e.stopPropagation(); onOpen(a.cla, a.progress || state.key, 'pulldown'); }}>
                          <PulldownIcon sx={{ fontSize: 20 }} />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Download Excel template (filled with current scores)">
                        <IconButton size="small" sx={{ color: state.to }}
                          onClick={(e) => { e.stopPropagation(); onExport?.(a.cla, state.key); }}>
                          <ExportIcon sx={{ fontSize: 20 }} />
                        </IconButton>
                      </Tooltip>
                    </Stack>
                  </Card>
                </Grow>
              </Grid>
            ))}
          </Grid>
        </Box>
      ))}
    </Box>
  );
}

function SummaryStat({ icon, label, value, tint, chip }) {
  return (
    <Stack direction="row" spacing={1.4} alignItems="center">
      <Box sx={{
        width: 42, height: 42, borderRadius: 2, flexShrink: 0,
        bgcolor: alpha(tint, 0.12), color: tint,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}>
        {icon}
      </Box>
      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Typography sx={{
          fontSize: 10.5, fontWeight: 800, letterSpacing: 0.6,
          textTransform: 'uppercase', color: 'text.secondary', lineHeight: 1,
        }}>
          {label}
        </Typography>
        <Stack direction="row" alignItems="baseline" spacing={1} sx={{ mt: 0.4 }}>
          <Typography sx={{
            fontWeight: 900, fontSize: { xs: 16, sm: 18 }, color: 'text.primary',
            lineHeight: 1.15, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
          }}>
            {value}
          </Typography>
          {chip}
        </Stack>
      </Box>
    </Stack>
  );
}

// Per-card progress bar. Shows the share of students in the class
// who already have at least one score saved for this (subject, exam
// type). Colour matches the section tint so a half-full Term Exam
// bar stays indigo, a Mock bar stays green, etc.
function ProgressMeter({ progress, from, to }) {
  const p = progress || { entered: 0, total: 0, pct: 0 };
  const pct = Math.max(0, Math.min(100, Number(p.pct) || 0));
  const isDone = pct === 100;
  const isEmpty = pct === 0;
  return (
    <Box sx={{ mt: 1.4 }}>
      <Stack direction="row" alignItems="center" spacing={0.8} sx={{ mb: 0.5 }}>
        <Typography sx={{
          fontSize: 10.5, fontWeight: 800, letterSpacing: 0.4,
          textTransform: 'uppercase',
          color: isDone ? 'success.main' : isEmpty ? 'text.disabled' : 'text.secondary',
        }}>
          {isDone ? 'Done' : isEmpty ? 'Not started' : 'In progress'}
        </Typography>
        <Box sx={{ flex: 1 }} />
        <Typography sx={{
          fontSize: 11, fontWeight: 800, color: to, letterSpacing: 0.2,
        }}>
          {p.entered} / {p.total}
        </Typography>
        <Typography sx={{
          fontSize: 11, fontWeight: 900, color: to, ml: 0.6,
        }}>
          {pct}%
        </Typography>
      </Stack>
      <LinearProgress
        variant="determinate"
        value={pct}
        sx={{
          height: 7, borderRadius: 99,
          bgcolor: alpha(from, 0.14),
          '& .MuiLinearProgress-bar': {
            borderRadius: 99,
            background: `linear-gradient(90deg, ${from}, ${to})`,
          },
        }} />
    </Box>
  );
}
